import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { projectId } from '../../utils/supabase/info';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { CheckCircle, XCircle, UserCheck, UserX, Loader2, Mail, Search } from 'lucide-react';
import { User } from '../../types';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export function VerificationManagement() {
  const { accessToken } = useAuth();
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [filterName, setFilterName] = useState('');
  const [filterRole, setFilterRole] = useState('');

  const serverUrl = `https://${projectId}.supabase.co/functions/v1/make-server-0b38861b`;

  const fetchPendingUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${serverUrl}/admin/pending-verifications`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPendingUsers(data.users || []);
      } else {
        toast.error('Failed to fetch pending verifications');
      }
    } catch (error) {
      console.error('Error fetching pending users:', error);
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken) {
      fetchPendingUsers();
    }
  }, [accessToken]);

  const handleVerify = async (userId: string, action: 'approve' | 'reject') => {
    try {
      setProcessingId(userId);
      const response = await fetch(`${serverUrl}/admin/verify-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ userId, action }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setPendingUsers(prev => prev.filter(u => u.id !== userId));
        window.dispatchEvent(new CustomEvent('verificationChanged'));
        
        if (action === 'approve') {
          if (data.emailSent) {
            toast.success('Verification email with login URL sent to user\'s email address', { duration: 5000 });
          } else {
            toast.warning('User approved but email notification could not be sent. Please notify the user manually with their login URL.', { duration: 8000 });
          }
        }
      } else {
        const error = await response.json();
        toast.error(error.error || `Failed to ${action} user`);
      }
    } catch (error) {
      console.error(`Error during ${action}:`, error);
      toast.error('Connection error');
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading pending registrations...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">User Verification</h1>
        <p className="text-muted-foreground">
          Approve or reject new student and faculty registrations.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Pending Approvals</CardTitle>
          <CardDescription>
            {pendingUsers.length} users are waiting for account verification.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filter Controls */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Filter by name..."
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
                className="pl-8"
              />
            </div>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="student">Student</SelectItem>
                <SelectItem value="faculty">Faculty</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {(() => {
            const filteredUsers = pendingUsers.filter((user) => {
              const matchesName = !filterName || user.name.toLowerCase().includes(filterName.toLowerCase());
              const matchesRole = !filterRole || filterRole === 'all' || user.role === filterRole;
              return matchesName && matchesRole;
            });

            if (filteredUsers.length === 0) {
              return (
                <div className="text-center py-12 border-2 border-dashed rounded-lg">
                  <UserCheck className="mx-auto h-12 w-12 text-muted-foreground/50" />
                  <h3 className="mt-4 text-lg font-semibold">
                    {pendingUsers.length === 0 ? 'All caught up!' : 'No matching users'}
                  </h3>
                  <p className="text-muted-foreground">
                    {pendingUsers.length === 0
                      ? 'No pending registrations at the moment.'
                      : 'No pending users match the current filters.'}
                  </p>
                </div>
              );
            }

            return (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>ID Number</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Dept/Course</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.idNumber}</TableCell>
                    <TableCell>
                      <Badge variant={user.role === 'faculty' ? 'secondary' : 'outline'}>
                        {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">
                      {user.department || user.course || 'N/A'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-destructive hover:bg-destructive/10"
                          onClick={() => handleVerify(user.id, 'reject')}
                          disabled={processingId === user.id}
                        >
                          <UserX className="h-4 w-4 mr-1" />
                          Reject
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleVerify(user.id, 'approve')}
                          disabled={processingId === user.id}
                        >
                          {processingId === user.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <UserCheck className="h-4 w-4 mr-1" />
                          )}
                          Approve & Notify
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            );
          })()}
        </CardContent>
      </Card>

      <div className="bg-muted/50 p-4 rounded-lg flex items-start gap-3 border border-border">
        <Mail className="h-5 w-5 text-primary mt-0.5" />
        <div className="text-sm">
          <p className="font-medium text-primary">Verification Email Notification</p>
          <p className="text-muted-foreground">
            When you approve a user, a verification email containing their registration details and a direct login URL will be sent to their registered address.
            Students and Faculty cannot log in until their account is verified by an administrator.
          </p>
        </div>
      </div>
    </div>
  );
}
